/**
 * Chakiboo Pack Selector
 *
 * Pack quantity UI + per-pair configurator. Source of truth for each Pair's
 * Size / Color / Graphic is `pairSelections` (not Dawn's hidden option inputs).
 * Dawn inputs may still be mirrored for theme compatibility only.
 */
if (!customElements.get('chakiboo-pack-selector')) {
  customElements.define(
    'chakiboo-pack-selector',
    class ChakibooPackSelector extends HTMLElement {
      connectedCallback() {
        this.packButtons = Array.from(this.querySelectorAll('[data-chakiboo-pack]'));
        this.pairItems = Array.from(this.querySelectorAll('[data-chakiboo-pair]'));
        this.pairSizeSelects = Array.from(this.querySelectorAll('[data-chakiboo-pair-size]'));
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        /** @type {Record<number, {size: string, color: string, graphic: string, sizeManual: boolean}>} */
        this.pairSelections = {};

        this.onPackClick = this.onPackClick.bind(this);
        this.onPairToggle = this.onPairToggle.bind(this);
        this.onPairSizeChange = this.onPairSizeChange.bind(this);
        this.onPairColorClick = this.onPairColorClick.bind(this);
        this.onPairGraphicClick = this.onPairGraphicClick.bind(this);
        this.onGlobalSizeChange = this.onGlobalSizeChange.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onFormSubmitCapture = this.onFormSubmitCapture.bind(this);
        this.onBuyNowClick = this.onBuyNowClick.bind(this);

        this.packButtons.forEach((button) => button.addEventListener('click', this.onPackClick));
        this.pairItems.forEach((pair) => {
          const toggle = pair.querySelector('[data-chakiboo-pair-toggle]');
          toggle?.addEventListener('click', this.onPairToggle);
        });
        this.pairSizeSelects.forEach((select) => {
          select.addEventListener('change', this.onPairSizeChange);
        });
        this.querySelectorAll('[data-chakiboo-pair-color]').forEach((button) => {
          button.addEventListener('click', this.onPairColorClick);
        });
        this.querySelectorAll('[data-chakiboo-pair-graphic]').forEach((button) => {
          button.addEventListener('click', this.onPairGraphicClick);
        });

        this.productInfo = this.closest('product-info');
        this.productInfo?.addEventListener('change', this.onGlobalSizeChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.variantData = this.parseVariantData();
        this.initPairSelections();

        const initialPack = Number(this.dataset.selectedPack || 3);
        this.setPack(initialPack, { expandFirst: true });
        this.applyGlobalSizeToNonManualPairs({ forceAll: true });
        this.renderAllPairUi();
        this.refreshPackPricesFromDom();
        this.bindCartActions();
      }

      disconnectedCallback() {
        this.packButtons.forEach((button) => button.removeEventListener('click', this.onPackClick));
        this.pairItems.forEach((pair) => {
          const toggle = pair.querySelector('[data-chakiboo-pair-toggle]');
          toggle?.removeEventListener('click', this.onPairToggle);
        });
        this.pairSizeSelects.forEach((select) => {
          select.removeEventListener('change', this.onPairSizeChange);
        });
        this.querySelectorAll('[data-chakiboo-pair-color]').forEach((button) => {
          button.removeEventListener('click', this.onPairColorClick);
        });
        this.querySelectorAll('[data-chakiboo-pair-graphic]').forEach((button) => {
          button.removeEventListener('click', this.onPairGraphicClick);
        });
        this.productInfo?.removeEventListener('change', this.onGlobalSizeChange);
        this.productInfo?.removeEventListener('submit', this.onFormSubmitCapture, true);
        this.productFormEl?.removeEventListener('click', this.onBuyNowClick);
        this.unsubscribeVariantChange?.();
      }

      /**
       * Seed independent Pair state from the rendered UI (Liquid defaults).
       * Dawn values are only a one-time fallback when a Pair has no selected control.
       */
      initPairSelections() {
        const dawnColor = this.getDawnOptionValue('color');
        const dawnGraphic = this.getDawnOptionValue('graphic');
        const dawnSize = this.getGlobalSizeValue();

        this.pairSelections = {};

        this.pairItems.forEach((pair) => {
          const pairIndex = Number(pair.dataset.chakibooPair);
          if (!pairIndex) return;

          const sizeSelect = pair.querySelector('[data-chakiboo-pair-size]');
          const selectedColor = pair.querySelector('[data-chakiboo-pair-color].is-selected');
          const selectedGraphic = pair.querySelector('[data-chakiboo-pair-graphic].is-selected');

          this.pairSelections[pairIndex] = {
            size: sizeSelect?.value || dawnSize || '',
            color: selectedColor?.dataset.optionValue || dawnColor || '',
            graphic: selectedGraphic?.dataset.optionValue || dawnGraphic || '',
            sizeManual: false,
          };
        });
      }

      /** Public accessor for upcoming multi-item ATC. */
      getPairSelections() {
        return structuredClone
          ? structuredClone(this.pairSelections)
          : JSON.parse(JSON.stringify(this.pairSelections));
      }

      getActivePairSelections() {
        const packCount = Number(this.dataset.selectedPack) || 1;
        const active = {};

        Object.keys(this.pairSelections).forEach((key) => {
          const pairIndex = Number(key);
          if (pairIndex <= packCount) {
            active[pairIndex] = { ...this.pairSelections[pairIndex] };
          }
        });

        return active;
      }

      ensurePairState(pairIndex) {
        if (!this.pairSelections[pairIndex]) {
          this.pairSelections[pairIndex] = {
            size: '',
            color: '',
            graphic: '',
            sizeManual: false,
          };
        }
        return this.pairSelections[pairIndex];
      }

      /**
       * Update source-of-truth state, re-render that Pair's UI, optionally mirror Dawn.
       * @param {number} pairIndex
       * @param {Partial<{size: string, color: string, graphic: string, sizeManual: boolean}>} patch
       * @param {{ mirrorDawn?: boolean, emit?: string }} [options]
       */
      setPairSelection(pairIndex, patch, { mirrorDawn = true, emit } = {}) {
        const state = this.ensurePairState(pairIndex);
        const next = { ...state, ...patch };
        this.pairSelections[pairIndex] = next;

        this.renderPairUi(pairIndex);

        if (mirrorDawn) {
          this.mirrorPairToDawn(pairIndex);
        }

        this.dispatchEvent(
          new CustomEvent('chakiboo:pair-selection-change', {
            bubbles: true,
            detail: {
              pairIndex,
              selection: { ...next },
              pairSelections: this.getPairSelections(),
              changed: emit || Object.keys(patch).join(','),
            },
          })
        );
      }

      renderAllPairUi() {
        this.pairItems.forEach((pair) => {
          const pairIndex = Number(pair.dataset.chakibooPair);
          if (pairIndex) this.renderPairUi(pairIndex);
        });
      }

      renderPairUi(pairIndex) {
        const pair = this.pairItems.find((el) => Number(el.dataset.chakibooPair) === pairIndex);
        const state = this.pairSelections[pairIndex];
        if (!pair || !state) return;

        const sizeSelect = pair.querySelector('[data-chakiboo-pair-size]');
        if (sizeSelect && state.size) {
          const hasOption = Array.from(sizeSelect.options).some((option) => option.value === state.size);
          if (hasOption && sizeSelect.value !== state.size) {
            sizeSelect.value = state.size;
          }
          sizeSelect.dataset.manualSize = state.sizeManual ? 'true' : 'false';
        }

        this.setPairOptionSelection(pair, '[data-chakiboo-pair-color]', state.color);
        this.setPairOptionSelection(pair, '[data-chakiboo-pair-graphic]', state.graphic);
      }

      setPairOptionSelection(pair, selector, value) {
        if (!pair || !value) return;

        pair.querySelectorAll(selector).forEach((button) => {
          const isSelected = button.dataset.optionValue === value;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
      }

      /**
       * Compatibility only: push one Pair's selection into Dawn hidden inputs.
       * Never used to rebuild `pairSelections` after init.
       */
      mirrorPairToDawn(pairIndex) {
        const state = this.pairSelections[pairIndex];
        if (!state) return;

        if (state.color) this.setDawnOptionValue('color', state.color);
        if (state.graphic) this.setDawnOptionValue('graphic', state.graphic);
      }

      getVariantSelects() {
        return this.productInfo?.querySelector('variant-selects') || null;
      }

      getGlobalSizeSelect() {
        return this.productInfo?.querySelector('[data-chakiboo-global-size]') || null;
      }

      getGlobalSizeValue() {
        return this.getGlobalSizeSelect()?.value || '';
      }

      /**
       * Resolve Dawn's Color / Graphic control (dropdown select or pill/swatch radios).
       * @param {'color'|'graphic'} kind
       */
      getDawnOptionControl(kind) {
        const root = this.getVariantSelects();
        if (!root) return null;

        const attr = kind === 'graphic' ? 'data-chakiboo-global-graphic' : 'data-chakiboo-global-color';
        const select = root.querySelector(`select[${attr}]`);
        if (select) {
          return { type: 'select', element: select, container: select.closest('.product-form__input') };
        }

        const container = root.querySelector(`[${attr}]`);
        if (!container) return null;

        const radios = Array.from(container.querySelectorAll('input[type="radio"]'));
        if (!radios.length) return null;

        return { type: 'radio', element: radios, container };
      }

      getDawnOptionValue(kind) {
        const control = this.getDawnOptionControl(kind);
        if (!control) return '';

        if (control.type === 'select') {
          return control.element.value || '';
        }

        const checked = control.element.find((input) => input.checked);
        return checked?.value || '';
      }

      setDawnOptionValue(kind, value) {
        if (!value) return false;

        const control = this.getDawnOptionControl(kind);
        if (!control) return false;

        if (control.type === 'select') {
          const select = control.element;
          const hasOption = Array.from(select.options).some((option) => option.value === value);
          if (!hasOption) return false;
          if (select.value === value) return true;

          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        const match = control.element.find((input) => input.value === value);
        if (!match) return false;
        if (match.checked) return true;

        match.checked = true;
        match.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }

      getSelectedVariantFromDom() {
        const script = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
        if (!script?.textContent) return null;
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          return null;
        }
      }

      isEventForThisSection(event) {
        const sectionId = event?.data?.sectionId;
        if (!sectionId) return true;

        const productSection =
          this.productInfo?.dataset?.originalSection || this.productInfo?.dataset?.section || this.dataset.section;

        return sectionId === productSection || sectionId === this.dataset.section;
      }

      formatMoney(cents, formatOverride) {
        const value = (Number(cents) / 100).toFixed(2);
        const format = formatOverride || this.moneyFormat || '${{amount}}';

        const withSeparators = (amount, thou = ',', dec = '.') => {
          const parts = amount.split('.');
          parts[0] = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thou}`);
          return parts.join(dec);
        };

        switch (format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1]) {
          case 'amount':
            return format.replace(/\{\{\s*amount\s*\}\}/, withSeparators(value));
          case 'amount_no_decimals':
            return format.replace(/\{\{\s*amount_no_decimals\s*\}\}/, withSeparators(value.split('.')[0]));
          case 'amount_with_comma_separator':
            return format.replace(
              /\{\{\s*amount_with_comma_separator\s*\}\}/,
              withSeparators(value, '.', ',')
            );
          case 'amount_no_decimals_with_comma_separator':
            return format.replace(
              /\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/,
              withSeparators(value.split('.')[0], '.', ',')
            );
          case 'amount_with_apostrophe_separator':
            return format.replace(
              /\{\{\s*amount_with_apostrophe_separator\s*\}\}/,
              withSeparators(value, "'", '.')
            );
          default:
            return format.replace(/\{\{\s*\w+\s*\}\}/, withSeparators(value));
        }
      }

      updatePackPrices(unitPriceCents) {
        const unitPrice = Number(unitPriceCents) || 0;
        const discountByPack = {
          1: 0,
          2: Number(this.dataset.pack2Discount || this.getAttribute('data-pack2-discount')) || 0,
          3: Number(this.dataset.pack3Discount || this.getAttribute('data-pack3-discount')) || 0,
        };

        this.packButtons.forEach((button) => {
          const packCount = Number(button.dataset.chakibooPack) || 1;
          const discountCents =
            Number(discountByPack[packCount] ?? button.getAttribute('data-chakiboo-pack-discount')) || 0;
          const originalCents = unitPrice * packCount;
          const finalCents = Math.max(originalCents - discountCents, 0);
          const showDiscount = discountCents > 0;

          const priceEl = button.querySelector('[data-chakiboo-pack-price]');
          const compareEl = button.querySelector('[data-chakiboo-pack-compare]');
          const footerEl = button.querySelector('[data-chakiboo-pack-footer]');

          if (priceEl) {
            priceEl.textContent = this.formatMoney(finalCents);
          }

          if (compareEl) {
            if (showDiscount) {
              compareEl.textContent = this.formatMoney(originalCents);
              compareEl.classList.remove('hidden');
            } else {
              compareEl.textContent = '';
              compareEl.classList.add('hidden');
            }
          }

          if (footerEl) {
            // Footer text is Theme Editor–only; do not prepend dynamic SAVE amounts.
            const footerText =
              footerEl.dataset.footerText || footerEl.getAttribute('data-footer-text') || '';
            if (footerText) {
              footerEl.textContent = footerText;
              footerEl.classList.remove('hidden');
            } else {
              footerEl.textContent = '';
              footerEl.classList.add('hidden');
            }
          }
        });

        this.updateFromPrice(unitPrice);
      }

      updateFromPrice(unitPriceCents) {
        const fromEls = this.productInfo?.querySelectorAll('[data-chakiboo-from-price]') ?? [];
        if (!fromEls.length) return;

        const unitPrice = Number(unitPriceCents) || 0;
        const pack2Discount =
          Number(this.dataset.pack2Discount || this.getAttribute('data-pack2-discount')) || 0;
        const pack3Discount =
          Number(this.dataset.pack3Discount || this.getAttribute('data-pack3-discount')) || 0;
        const pack2Unit = Math.max(unitPrice * 2 - pack2Discount, 0) / 2;
        const pack3Unit = Math.max(unitPrice * 3 - pack3Discount, 0) / 3;
        const fromCents = Math.min(unitPrice, pack2Unit, pack3Unit);

        fromEls.forEach((el) => {
          const format = el.getAttribute('data-money-format') || this.moneyFormat;
          el.textContent = this.formatMoney(fromCents, format);
        });
      }

      refreshPackPricesFromDom() {
        const variant = this.getSelectedVariantFromDom();
        if (variant && variant.price != null) {
          this.updatePackPrices(variant.price);
        }
      }

      onPackClick(event) {
        const button = event.currentTarget;
        const packCount = Number(button.dataset.chakibooPack);
        if (!packCount) return;
        this.setPack(packCount, { expandFirst: true });
      }

      setPack(packCount, { expandFirst = false } = {}) {
        this.dataset.selectedPack = String(packCount);

        this.packButtons.forEach((button) => {
          const isSelected = Number(button.dataset.chakibooPack) === packCount;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.pairItems.forEach((pair) => {
          const pairIndex = Number(pair.dataset.chakibooPair);
          const isVisible = pairIndex <= packCount;
          pair.hidden = !isVisible;
          pair.classList.toggle('is-visible', isVisible);

          if (!isVisible) {
            pair.classList.remove('is-open');
            const toggle = pair.querySelector('[data-chakiboo-pair-toggle]');
            toggle?.setAttribute('aria-expanded', 'false');
          }
        });

        if (expandFirst) {
          const firstVisible = this.pairItems.find((pair) => !pair.hidden);
          this.pairItems.forEach((pair) => {
            const shouldOpen = pair === firstVisible;
            pair.classList.toggle('is-open', shouldOpen);
            pair.querySelector('[data-chakiboo-pair-toggle]')?.setAttribute(
              'aria-expanded',
              shouldOpen ? 'true' : 'false'
            );
          });
        }

        this.dispatchEvent(
          new CustomEvent('chakiboo:pack-change', {
            bubbles: true,
            detail: {
              packCount,
              pairSelections: this.getActivePairSelections(),
            },
          })
        );
      }

      onPairToggle(event) {
        const toggle = event.currentTarget;
        const pair = toggle.closest('[data-chakiboo-pair]');
        if (!pair || pair.hidden) return;

        const willOpen = !pair.classList.contains('is-open');
        pair.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      }

      onPairSizeChange(event) {
        const select = event.currentTarget;
        const pairIndex = Number(select.dataset.chakibooPairSize);
        if (!pairIndex) return;

        this.setPairSelection(
          pairIndex,
          { size: select.value, sizeManual: true },
          { mirrorDawn: false, emit: 'size' }
        );

        this.dispatchEvent(
          new CustomEvent('chakiboo:pair-size-change', {
            bubbles: true,
            detail: {
              pairIndex,
              size: select.value,
              manual: true,
              selection: { ...this.pairSelections[pairIndex] },
            },
          })
        );
      }

      onPairColorClick(event) {
        const button = event.currentTarget;
        const pair = button.closest('[data-chakiboo-pair]');
        const value = button.dataset.optionValue;
        const pairIndex = Number(pair?.dataset.chakibooPair);
        if (!pairIndex || !value) return;

        this.setPairSelection(pairIndex, { color: value }, { mirrorDawn: true, emit: 'color' });

        this.dispatchEvent(
          new CustomEvent('chakiboo:pair-color-change', {
            bubbles: true,
            detail: {
              pairIndex,
              color: value,
              selection: { ...this.pairSelections[pairIndex] },
            },
          })
        );
      }

      onPairGraphicClick(event) {
        const button = event.currentTarget;
        const pair = button.closest('[data-chakiboo-pair]');
        const value = button.dataset.optionValue;
        const pairIndex = Number(pair?.dataset.chakibooPair);
        if (!pairIndex || !value) return;

        this.setPairSelection(pairIndex, { graphic: value }, { mirrorDawn: true, emit: 'graphic' });

        this.dispatchEvent(
          new CustomEvent('chakiboo:pair-graphic-change', {
            bubbles: true,
            detail: {
              pairIndex,
              graphic: value,
              imageUrl: button.dataset.imageUrl || '',
              selection: { ...this.pairSelections[pairIndex] },
            },
          })
        );
      }

      onGlobalSizeChange(event) {
        const target = event.target;
        if (!(target instanceof HTMLSelectElement)) return;
        if (!target.hasAttribute('data-chakiboo-global-size')) return;
        this.applyGlobalSizeToNonManualPairs();
      }

      onVariantChange(event) {
        if (!this.isEventForThisSection(event)) return;

        this.applyGlobalSizeToNonManualPairs();

        const variant = event?.data?.variant || this.getSelectedVariantFromDom();
        if (variant && variant.price != null) {
          this.updatePackPrices(variant.price);
        }
      }

      /**
       * Step 1 Size → non-manual Pair sizes in `pairSelections`, then UI.
       * Does not read Color/Graphic back from Dawn.
       */
      applyGlobalSizeToNonManualPairs({ forceAll = false } = {}) {
        const globalValue = this.getGlobalSizeValue();
        if (!globalValue) return;

        Object.keys(this.pairSelections).forEach((key) => {
          const pairIndex = Number(key);
          const state = this.pairSelections[pairIndex];
          if (!state) return;
          if (!forceAll && state.sizeManual) return;

          this.pairSelections[pairIndex] = {
            ...state,
            size: globalValue,
            sizeManual: forceAll ? false : state.sizeManual,
          };
          this.renderPairUi(pairIndex);
        });
      }

      /* ------------------------------------------------------------------ */
      /* Multi-variant Add to Cart / Buy Now                                */
      /* ------------------------------------------------------------------ */

      parseVariantData() {
        const script = this.querySelector('[data-chakiboo-pack-variants]');
        if (!script?.textContent) {
          return {
            productId: null,
            options: [],
            optionIndexes: { size: -1, color: -1, graphic: -1 },
            hasColor: false,
            hasGraphic: false,
            variants: [],
          };
        }

        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('[chakiboo-pack] Failed to parse variants JSON', error);
          return {
            productId: null,
            options: [],
            optionIndexes: { size: -1, color: -1, graphic: -1 },
            hasColor: false,
            hasGraphic: false,
            variants: [],
          };
        }
      }

      bindCartActions() {
        const attach = () => {
          if (!this.productInfo) {
            this.productInfo = this.closest('product-info');
          }
          if (!this.productInfo) return false;

          this.productInfo.removeEventListener('submit', this.onFormSubmitCapture, true);
          this.productInfo.addEventListener('submit', this.onFormSubmitCapture, true);

          this.productFormEl = this.productInfo.querySelector('product-form');
          if (this.productFormEl) {
            this.productFormEl.removeEventListener('click', this.onBuyNowClick);
            this.productFormEl.addEventListener('click', this.onBuyNowClick);
          }

          return Boolean(this.productFormEl);
        };

        if (!attach()) {
          requestAnimationFrame(() => attach());
          document.addEventListener('DOMContentLoaded', attach, { once: true });
        }
      }

      getPackCount() {
        return Number(this.dataset.selectedPack) || 1;
      }

      getPackLabel(packCount = this.getPackCount()) {
        const button = this.packButtons.find((el) => Number(el.dataset.chakibooPack) === packCount);
        const title = button?.querySelector('.chakiboo-pack__card-title')?.textContent?.trim();
        return title || `${packCount}-Pack`;
      }

      createPackGroupId() {
        return `pack-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      }

      getVariantOptionValue(variant, optionKey) {
        const index = this.variantData?.optionIndexes?.[optionKey];
        if (index == null || index < 0) return null;
        if (index === 0) return variant.option1;
        if (index === 1) return variant.option2;
        if (index === 2) return variant.option3;
        return null;
      }

      /**
       * Resolve one Pair's Size + Color + Graphic to an exact Shopify variant.
       * @returns {{ variant: object|null, error: string|null }}
       */
      resolveVariantForPair(pairIndex, selection) {
        const hasColor = Boolean(this.variantData?.hasColor);
        const hasGraphic = Boolean(this.variantData?.hasGraphic);
        const size = (selection?.size || '').trim();
        const color = (selection?.color || '').trim();
        const graphic = (selection?.graphic || '').trim();

        if (!size) {
          return { variant: null, error: `Pair ${pairIndex}: please choose a Size.` };
        }
        if (hasColor && !color) {
          return { variant: null, error: `Pair ${pairIndex}: please choose a Color.` };
        }
        if (hasGraphic && !graphic) {
          return { variant: null, error: `Pair ${pairIndex}: please choose a Graphic.` };
        }

        const variants = this.variantData?.variants || [];
        const match = variants.find((variant) => {
          const variantSize = this.getVariantOptionValue(variant, 'size');
          if (variantSize != null && variantSize !== size) return false;

          if (hasColor) {
            const variantColor = this.getVariantOptionValue(variant, 'color');
            if (variantColor != null && variantColor !== color) return false;
          }

          if (hasGraphic) {
            const variantGraphic = this.getVariantOptionValue(variant, 'graphic');
            if (variantGraphic != null && variantGraphic !== graphic) return false;
          }

          return true;
        });

        if (!match) {
          const parts = [size];
          if (hasColor) parts.push(color);
          if (hasGraphic) parts.push(graphic);
          return {
            variant: null,
            error: `Pair ${pairIndex}: no matching product variant for ${parts.join(' / ')}.`,
          };
        }

        if (match.available === false) {
          return {
            variant: null,
            error: `Pair ${pairIndex}: ${match.title || 'that variant'} is sold out.`,
          };
        }

        return { variant: match, error: null };
      }

      /**
       * Build cart line items for the active pack. Returns null on validation failure.
       */
      buildPackCartItems() {
        const packCount = this.getPackCount();
        const active = this.getActivePairSelections();
        const activeKeys = Object.keys(active)
          .map(Number)
          .sort((a, b) => a - b);

        if (activeKeys.length !== packCount) {
          this.showCartError(`Please configure all ${packCount} Pair${packCount > 1 ? 's' : ''} for this pack.`);
          return null;
        }

        const packLabel = this.getPackLabel(packCount);
        const packGroup = this.createPackGroupId();
        const items = [];

        for (const pairIndex of activeKeys) {
          const { variant, error } = this.resolveVariantForPair(pairIndex, active[pairIndex]);
          if (error || !variant?.id) {
            this.showCartError(error || `Pair ${pairIndex}: could not resolve a product variant.`);
            return null;
          }

          items.push({
            id: Number(variant.id),
            quantity: 1,
            properties: {
              Pack: packLabel,
              Pair: String(pairIndex),
              _pack_group: packGroup,
            },
          });
        }

        if (items.length !== packCount) {
          this.showCartError(`Expected ${packCount} cart lines but resolved ${items.length}.`);
          return null;
        }

        return items;
      }

      showCartError(message) {
        const productForm = this.productInfo?.querySelector('product-form');
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(message);
          return;
        }

        const wrapper = this.productInfo?.querySelector('.product-form__error-message-wrapper');
        const text = wrapper?.querySelector('.product-form__error-message');
        if (wrapper && text) {
          wrapper.removeAttribute('hidden');
          text.textContent = message;
          return;
        }

        window.alert(message);
      }

      clearCartError() {
        const productForm = this.productInfo?.querySelector('product-form');
        if (productForm && typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage();
        }
      }

      setCartLoading(isLoading) {
        const productForm = this.productInfo?.querySelector('product-form');
        const submitButton = productForm?.querySelector('[type="submit"][name="add"]');
        const spinner = productForm?.querySelector('.loading__spinner');
        const buyNowButton = productForm?.querySelector('.buy-now-button');

        if (submitButton) {
          if (isLoading) {
            submitButton.setAttribute('aria-disabled', 'true');
            submitButton.classList.add('loading');
          } else {
            submitButton.classList.remove('loading');
            if (!productForm?.error) submitButton.removeAttribute('aria-disabled');
          }
        }

        if (spinner) {
          spinner.classList.toggle('hidden', !isLoading);
        }

        if (buyNowButton) {
          buyNowButton.toggleAttribute('disabled', isLoading);
          buyNowButton.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
        }
      }

      onFormSubmitCapture(event) {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
        if (!this.productInfo?.contains(form)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.addPackToCart({ checkout: false });
      }

      onBuyNowClick(event) {
        const button = event.target.closest('.buy-now-button');
        if (!button || !this.productFormEl?.contains(button)) return;

        event.preventDefault();
        event.stopPropagation();
        this.addPackToCart({ checkout: true });
      }

      async addPackToCart({ checkout = false } = {}) {
        if (this._cartRequestInFlight) return;

        this.clearCartError();
        const items = this.buildPackCartItems();
        if (!items) return;

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const body = { items };

        if (cart && typeof cart.getSectionsToRender === 'function') {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        this._cartRequestInFlight = true;
        this.setCartLoading(true);

        try {
          const response = await fetch(window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
          });

          const data = await response.json();

          if (!response.ok || data.status) {
            const message =
              data.description ||
              data.message ||
              (typeof data.errors === 'string' ? data.errors : null) ||
              'Could not add pack to cart.';
            this.showCartError(message);
            const productForm = this.productInfo?.querySelector('product-form');
            if (productForm) productForm.error = true;
            return;
          }

          const productForm = this.productInfo?.querySelector('product-form');
          if (productForm) productForm.error = false;

          if (checkout) {
            window.location.href = '/checkout';
            return;
          }

          if (!cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            await publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'chakiboo-pack-selector',
              productVariantId: items[0]?.id,
              cartData: data,
            });
          }

          cart.classList?.remove('is-empty');

          if (data.sections && typeof cart.renderContents === 'function') {
            cart.renderContents(data);
          } else if (typeof cart.open === 'function') {
            cart.open();
          } else {
            window.location = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          console.error('[chakiboo-pack] Add to cart failed', error);
          this.showCartError('Could not add pack to cart. Please try again.');
        } finally {
          this._cartRequestInFlight = false;
          this.setCartLoading(false);
        }
      }
    }
  );
}
